import { DestroyRef, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, of, pipe, switchMap } from 'rxjs';
import {
  GetShieldZoneUploadScanningMetricsResponse,
  MetricsService,
  ShieldApiStore,
  UpdateUploadScanningConfigurationRequest,
  UploadScanningConfigurationState,
  UploadScanningScannerMode,
  UploadScanningService,
} from '@bn/app-shield';
import { getStoreEvents, StoreRequest, withDefaultRequestStatusState } from '@bn/shared/state-store';
import { MapShieldUploadScanningChartData, ShieldUploadScanningChartData } from './helpers/shield-api-store.helper';

// Define events for store actions
const events = getStoreEvents('SHIELD_UPLOAD_SCANNING', ['SHIELD_UPLOAD_SCANNING', 'SHIELD_UPLOAD_SCANNING_UPDATE', 'SHIELD_UPLOAD_SCANNING_METRICS']);

export interface UploadScanningState extends UploadScanningConfigurationState {
  chartMetrics: ShieldUploadScanningChartData | undefined;
}

const initialState: UploadScanningState = {
  shieldZoneId: undefined,
  isEnabled: false,
  csamScanningMode: UploadScanningScannerMode.Disabled,
  antivirusScanningMode: UploadScanningScannerMode.Disabled,
  chartMetrics: undefined,
};

export const ShieldUploadScanningApiStore = signalStore(
  { providedIn: 'root' },
  withDevtools('Shield.UploadScanning'),
  withState<UploadScanningState>(initialState),
  withDefaultRequestStatusState(),
  withMethods((store, metricsService = inject(MetricsService), destroyRef = inject(DestroyRef)) => ({
    getUploadScanningChartMetrics: rxMethod<{ shieldZoneId: number }>(
      pipe(
        switchMap(({ shieldZoneId }) => {
          updateState(store, events.SHIELD_UPLOAD_SCANNING_METRICS.REQUEST, store.setLoading(StoreRequest.Item, ['chartMetrics']));

          return metricsService.getShieldZoneUploadScanningMetrics({ shieldZoneId }).pipe(
            takeUntilDestroyed(destroyRef),
            tapResponse({
              next: (response: GetShieldZoneUploadScanningMetricsResponse) => {
                const { data, error } = response;

                if (error && !error?.success) {
                  updateState(
                    store,
                    events.SHIELD_UPLOAD_SCANNING_METRICS.ERROR,
                    store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics`, ['chartMetrics']),
                  );
                }

                updateState(
                  store,
                  events.SHIELD_UPLOAD_SCANNING_METRICS.SUCCESS,
                  { chartMetrics: MapShieldUploadScanningChartData(data) },
                  store.setFinished(StoreRequest.Item, ['chartMetrics']),
                );
              },
              error: ({ error }) =>
                updateState(
                  store,
                  events.SHIELD_UPLOAD_SCANNING_METRICS.ERROR,
                  store.setErrorMessage(StoreRequest.Item, `Error while fetching Chart metrics ${error.message}`, ['chartMetrics']),
                ),
            }),
          );
        }),
      ),
    ),
  })),

  withMethods((store, uploadScanningService = inject(UploadScanningService), destroyRef = inject(DestroyRef)) => ({
    getUploadScanning: rxMethod<{ shieldZoneId: number; cleanRequest?: boolean }>(
      exhaustMap(({ shieldZoneId, cleanRequest }) => {
        const shieldId = store.shieldZoneId ? store.shieldZoneId() : null;

        if (shieldId === shieldZoneId && !cleanRequest) {
          return of(null);
        }

        updateState(store, events.SHIELD_UPLOAD_SCANNING.REQUEST, store.setLoading(StoreRequest.Item));

        return uploadScanningService.getUploadScanning({ shieldZoneId }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.SHIELD_UPLOAD_SCANNING.ERROR,
                  store.setErrorMessage(StoreRequest.List, error?.message ?? 'There was an error while trying to get Upload Scanning data.'),
                );
              }

              if (shieldZoneId && !isNaN(shieldZoneId)) store.getUploadScanningChartMetrics({ shieldZoneId });

              updateState(
                store,
                events.SHIELD_UPLOAD_SCANNING.SUCCESS,
                {
                  shieldZoneId: data?.shieldZoneId,
                  isEnabled: data?.isEnabled,
                  csamScanningMode: data?.csamScanningMode,
                  antivirusScanningMode: data?.antivirusScanningMode,
                },
                store.setFinished(StoreRequest.Item),
              );
            },
            error: ({ error }) => updateState(store, events.SHIELD_UPLOAD_SCANNING.ERROR, store.setError(StoreRequest.Item, error)),
          }),
        );
      }),
    ),
  })),
  withMethods((store, uploadScanningService = inject(UploadScanningService), destroyRef = inject(DestroyRef)) => ({
    updateUploadScanning: rxMethod<{
      shieldZoneId: number;
      data: Partial<UpdateUploadScanningConfigurationRequest>;
      customFields?: string[];
    }>(
      switchMap(({ shieldZoneId, data, customFields }) => {
        updateState(store, events.SHIELD_UPLOAD_SCANNING_UPDATE.REQUEST, store.setLoading(StoreRequest.Update, customFields ? customFields : undefined));

        return uploadScanningService.updateUploadScanning({ shieldZoneId, updateUploadScanningConfigurationRequest: data }).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              const { error, data } = response;

              if (error && !error?.success) {
                updateState(
                  store,
                  events.SHIELD_UPLOAD_SCANNING_UPDATE.ERROR,
                  store.setErrorMessage(
                    StoreRequest.Update,
                    error?.message ?? 'There was an error while trying to get Access List data.',
                    customFields ? customFields : undefined,
                  ),
                );
              }

              updateState(
                store,
                events.SHIELD_UPLOAD_SCANNING.SUCCESS,
                {
                  shieldZoneId: data?.shieldZoneId,
                  isEnabled: data?.isEnabled,
                  csamScanningMode: data?.csamScanningMode,
                  antivirusScanningMode: data?.antivirusScanningMode,
                },
                store.setFinished(StoreRequest.Update, customFields ? customFields : undefined),
              );
            },
            error: ({ error }) =>
              updateState(
                store,
                events.SHIELD_UPLOAD_SCANNING_UPDATE.ERROR,
                store.setError(StoreRequest.Update, error, customFields ? customFields : undefined),
              ),
          }),
        );
      }),
    ),
  })),

  withHooks({
    onInit(store) {
      const shieldStore = inject(ShieldApiStore);

      effect(() => {
        const id = Number(shieldStore.selectedEntityId()()) || null;

        untracked(() => {
          if (id)
            store.getUploadScanning({
              shieldZoneId: id,
              cleanRequest: true,
            });
        });
      });
    },
  }),
);
